import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import PageContainer from '../../components/container/PageContainer';
import { useDispatch, useSelector } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate } from 'react-router-dom';
import { getTrays, createTray, updateTray, deleteTray } from '../../reducers/trays/traysActions';
import { useToken } from '../../hooks/useToken';
import CreateTrayModal from '../../components/apps/trays/CreateTrayModal';
import TrayCard from '../../components/apps/trays/TrayCard';

const BCrumb = [
  { title: 'Inicio', onClick: true },
  { title: 'Bandejas' },
];

/**
 * Vista de gestión de bandejas (trays): listar, crear, editar y eliminar.
 * La operación de cola (tomar/completar contactos) vive en el chat.
 */
const TraysList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { getTokenSilent } = useToken();
  const agent = useSelector(state => state.agent);
  const { items: trays, loading } = useSelector(state => state.trays);

  const [isLoading, setIsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTray, setEditingTray] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const showNotification = (message, severity = 'info') =>
    setNotification({ open: true, message, severity });
  const handleCloseNotification = () =>
    setNotification(prev => ({ ...prev, open: false }));

  const loadTrays = async () => {
    try {
      const id_customer = agent?.id_customer;
      if (!id_customer) return;
      const token = await getTokenSilent();
      await dispatch(getTrays({ id_customer, limit: 100, token }));
    } catch (error) {
      console.error('Error cargando bandejas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTrays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id_customer]);

  const handleCreate = async (data) => {
    const token = await getTokenSilent();
    await dispatch(createTray({ ...data, id_customer: String(agent.id_customer), token }));
    showNotification('Bandeja creada', 'success');
    loadTrays();
  };

  const handleUpdate = async (data) => {
    const token = await getTokenSilent();
    await dispatch(updateTray(editingTray.id, { ...data, token }));
    showNotification('Bandeja actualizada', 'success');
    setEditingTray(null);
    loadTrays();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const token = await getTokenSilent();
      await dispatch(deleteTray(deleteTarget.id, token));
      showNotification('Bandeja eliminada', 'success');
      setDeleteTarget(null);
      setEditingTray(null);
    } catch (error) {
      showNotification(error?.response?.data?.message || error.message || 'Error al eliminar la bandeja', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleBreadcrumbNavigation = (event, itemTitle) => {
    event.preventDefault();
    event.stopPropagation();
    if (itemTitle === 'Inicio') navigate('/admin/chat');
  };

  const CustomBreadcrumb = () => (
    <Grid
      container
      sx={{ padding: '2px', paddingLeft: '15px', marginBottom: '2px' }}
    >
      <Grid item xs={12} sm={6} lg={8}>
        <Typography fontWeight="700" variant="h3" sx={{ lineHeight: '1.235' }}>
          Bandejas
        </Typography>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          {BCrumb.map((item) => (
            <div key={item.title}>
              {item.onClick ? (
                <Link
                  underline="none"
                  color="inherit"
                  component="button"
                  onClick={(e) => handleBreadcrumbNavigation(e, item.title)}
                  sx={{ cursor: 'pointer', border: 'none', background: 'none', fontSize: 'inherit' }}
                >
                  {item.title}
                </Link>
              ) : (
                <Typography color="textPrimary">{item.title}</Typography>
              )}
            </div>
          ))}
        </Breadcrumbs>
      </Grid>
    </Grid>
  );

  return (
    <PageContainer title="Bandejas" description="Lista de bandejas de contacto disponibles">
      <CustomBreadcrumb />

      <Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end" mb={3}>
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Nueva Bandeja
          </Button>
        </Box>

        {isLoading || loading ? (
          <Box display="flex" justifyContent="center" m={4}>
            <CircularProgress />
          </Box>
        ) : trays.length === 0 ? (
          <Box textAlign="center" my={5}>
            <Typography variant="h4" color="textSecondary" gutterBottom>
              No hay bandejas disponibles
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Crea una nueva bandeja para comenzar
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {trays.map((tray) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={tray.id}>
                <TrayCard tray={tray} onViewDetails={setEditingTray} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Modal de creación */}
      <CreateTrayModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
      />

      {/* Modal de edición (y borrado) */}
      <CreateTrayModal
        open={Boolean(editingTray)}
        tray={editingTray}
        onClose={() => setEditingTray(null)}
        onSave={handleUpdate}
        onDelete={() => setDeleteTarget(editingTray)}
      />

      {/* Confirmación de borrado */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Eliminar bandeja</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ¿Eliminar la bandeja <strong>{deleteTarget?.name}</strong>? Los contactos ya
            asignados a esta bandeja no se eliminarán.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default TraysList;
